import React, { useState, useCallback } from "react";
import Table from "UIComponents/Table";
import {
  CloseCircleFilled,
  EyeHiddenOutlined,
  EyeOutlined,
  TickCircleFilled,
} from "@toddle-edu/ds-icons";
import { Button } from "@toddle-edu/ds-web";
import classes from "./TableQuestionWrapper.scss";
import {
  FIELD_UID_TABLE,
  FIELD_UID_TABLE_ANSWERS,
} from "Worksheet/modules/WorksheetConstants";
import _ from "lodash";
import {
  getTableAnswers,
  extractTextWithLatex,
} from "Worksheet/modules/WorksheetUtils";
import {
  TABLE_INPUT_END_CHAR,
  TABLE_INPUT_START_CHAR,
} from "UIComponents/Table/constants";
import { htmlToText } from "Utils";
import { getIsWorksheetDocEditorEnabled } from "Worksheet/modules/WorksheetFeatureFlags";

const correctAnswerCell = classes.surfaceSuccess;
const inCorrectAnswerCell = classes.surfaceError;
const suffixElementInCell = classes.suffixElmentClass;

const TableQuestionWrapper = React.memo(
  ({ mode, value, updateInputField, t, isSubmitted }) => {
    const showDocEditor = getIsWorksheetDocEditorEnabled();

    const [viewSolution, setViewSolution] = useState(false);
    const toggleViewSolution = () => {
      setViewSolution(prev => !prev);
    };

    const additionalClassesTable = [];
    const suffixElementTable = [];
    const evaluationValue = {
      [FIELD_UID_TABLE]: _.cloneDeep(_.get(value, FIELD_UID_TABLE)),
    };
    const { data = [], headerType = [] } = _.get(evaluationValue, [
      FIELD_UID_TABLE,
    ]);
    const evaluationView = _.isEqual(mode, "evaluate");

    if (evaluationView) {
      if (isSubmitted) {
        _.map(getTableAnswers(data, headerType), row => {
          const rowId = _.get(row, "id");
          const stylesObj = { id: rowId };
          const suffixElementRow = { id: rowId };
          if (!viewSolution) {
            _.forEach(row, (cell, colId) => {
              if (_.isEqual(colId, "id")) return;
              const cellTextWithLatex = extractTextWithLatex(cell);
              const studentResponse = _.toLower(
                _.trim(cellTextWithLatex.slice(1, -1))
              );
              const responseRow = _.find(
                _.get(value, FIELD_UID_TABLE_ANSWERS),
                qRow => _.isEqual(_.get(qRow, "id"), rowId)
              );
              const cellAnswer = _.trim(
                extractTextWithLatex(_.get(responseRow, [colId]))
              );
              const cellAnserArray = _.split(
                cellAnswer.substring(1, _.size(cellAnswer) - 1),
                ";"
              );
              const formattedAnswerArray = [];
              _.forEach(cellAnserArray, item => {
                const answer = _.toLower(_.trim(item));
                if (!_.isEmpty(answer)) {
                  formattedAnswerArray.push(answer);
                }
              });
              if (_.includes(formattedAnswerArray, studentResponse)) {
                stylesObj[colId] = [correctAnswerCell, suffixElementInCell];
                suffixElementRow[colId] = (
                  <TickCircleFilled variant="success" />
                );
              } else {
                stylesObj[colId] = [inCorrectAnswerCell, suffixElementInCell];
                suffixElementRow[colId] = (
                  <CloseCircleFilled variant="critical" />
                );
              }
            });
          }
          additionalClassesTable.push(stylesObj);
          suffixElementTable.push(suffixElementRow);
        });
      }
    }

    if (showDocEditor && (viewSolution || _.isEqual(mode, "preview"))) {
      const formattedTableAnswers = _.cloneDeep(
        _.get(value, FIELD_UID_TABLE_ANSWERS)
      );

      _.set(evaluationValue, [FIELD_UID_TABLE, "data"], formattedTableAnswers);
    }

    if (!showDocEditor && (viewSolution || _.isEqual(mode, "preview"))) {
      const formattedTableAnswers = _.cloneDeep(
        _.get(value, FIELD_UID_TABLE_ANSWERS)
      );

      _.forEach(formattedTableAnswers, row => {
        const rowId = _.get(row, "id");
        _.forEach(row, (cellVal, key) => {
          const cell = _.trim(htmlToText(cellVal));
          if (_.isEqual(key, "id")) return;
          const isAnswerCell =
            _.startsWith(cell, TABLE_INPUT_START_CHAR) &&
            _.endsWith(cell, TABLE_INPUT_END_CHAR);

          if (isAnswerCell) {
            const cellAnserArray = _.split(
              cell.substring(1, _.size(cell) - 1),
              ";"
            );
            let formattedAnswerString = "";
            _.forEach(cellAnserArray, (item, i) => {
              formattedAnswerString += `${_.trim(item)}${
                +_.isEqual(i, _.size(cellAnserArray) - 1) ? "" : ";"
              }`;
            });
            const row = _.find(formattedTableAnswers, ({ id }) => {
              return id === rowId;
            });

            row[key] =
              TABLE_INPUT_START_CHAR +
              formattedAnswerString +
              TABLE_INPUT_END_CHAR;
          }
        });
      });

      _.set(evaluationValue, [FIELD_UID_TABLE, "data"], formattedTableAnswers);
    }
    const updateTableData = useCallback(
      ({ headerType, columns, data, columnOrder }) => {
        const orderedCol = [];
        _.map(columnOrder, col => {
          const colObj = _.find(columns, column => {
            return _.get(column, "id") === col;
          });
          orderedCol.push(colObj);
        });
        if (mode === "teacher-preview") {
          return;
        } else if (mode === "create") {
          updateInputField({
            params: {
              tableAnswers: {
                headerType: headerType,
                columns: orderedCol,
                data: data,
              },
            },
            isDebounce: true,
            isMutation: true,
          });
        } else {
          updateInputField({
            params: {
              table: {
                headerType: headerType,
                columns: orderedCol,
                data: data,
              },
            },
            isDebounce: true,
            isMutation: true,
          });
        }
      },
      [mode, updateInputField]
    );

    return (
      <>
        <Table
          mode={mode}
          value={evaluationValue}
          isSubmitted={isSubmitted}
          additionalClassesTable={additionalClassesTable}
          suffixElementTable={suffixElementTable}
          updateTableData={updateTableData}
          maxColumnSize={6}
          maxRowSize={10}
          shouldHighlightInputText={true}
          testId="worksheet"
        />
        {/* View solution will be visible for teacher evaluation mode to view correct answer mode */}
        {mode === "evaluate" && (
          <div className={classes.viewSolutionBtnWrapper}>
            <Button
              size="medium"
              variant="neutral"
              type="outlined"
              dsVersion="2.0"
              _legacy_size="small"
              _legacy_variant="outlined"
              icon={viewSolution ? <EyeHiddenOutlined /> : <EyeOutlined />}
              onClick={toggleViewSolution}
            >
              {t(
                viewSolution
                  ? "worksheet:hide_solution"
                  : "worksheet:view_solution"
              )}
            </Button>
          </div>
        )}
      </>
    );
  }
);

TableQuestionWrapper.displayName = "TableQuestionWrapper";

export default TableQuestionWrapper;
